import * as Icons from '@mui/icons-material';
import { Box, Typography, useMediaQuery, useTheme } from '@mui/material';
import { EGender } from '@sharedInterfaces/IEmployee';
import { IComponentState } from '@sharedInterfaces/IEmployeeTalkState';
import {
    ETemplateCategory,
    type IEmployeeTalkTemplateColumn,
    type IEmployeeTalkTemplateComponent,
    IEmployeeTalkTemplatePage,
    type IEmployeeTalkTemplateSection,
} from '@sharedInterfaces/IEmployeeTalkTemplate';
import { getLanguageTexts } from '@sharedInterfaces/Language/languageHelper';
import Button from '@src/App/Components/Buttons/Button/Button';
import type { AppState } from '@store/store';
import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { getComponentByType } from '../TemplateComponents/ETTComponentFactory';
import { useEmployeeTalk } from '../TemplateComponents/ETTEmployeeTalkProvider';
import styles from '../TemplateEditor/ETTTemplateEditor.module.css';
import type { ITemplate } from '../TemplateEditor/types';

export interface IETTTemplatePreviewProps {
    template?: ITemplate;
    initialStep?: number;
    onStepChange?: (step: number) => void;
    onSubmit?: (data: Record<string, unknown>) => void;
    isOnboarding?: boolean;
    onCVUploadStateChange?: (pageIndex: number, isUploaded: boolean) => void;
    providerContext?: ReturnType<typeof useEmployeeTalk>;
    employeeTalkId: number;
}

export const ETTTemplatePreview: React.FC<IETTTemplatePreviewProps> = ({
    template,
    initialStep = 0,
    onStepChange,
    onSubmit,
    isOnboarding,
    onCVUploadStateChange,
    providerContext,
    employeeTalkId,
}) => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const lang = useSelector((state: AppState) => state.employee.language);
    const langStrings = getLanguageTexts(lang).employeeTalk;
    const oftenUsed = langStrings.oftenUsed;

    // CRITICAL: Use provided context to prevent duplicate WebSocket subscriptions
    // PROBLEM: Multiple useEmployeeTalk() calls created multiple WebSocket connections
    // SOLUTION: Pass context from parent to avoid duplicate subscriptions
    // SMALL CHANGE, BIG IMPACT: This prevents infinite WebSocket connection loops
    // Related: See useSecureEmployeeTalkState.ts for subscription management details
    const employeeTalkContext = providerContext || useEmployeeTalk();
    const { talkState, currentStep, getComponentData, updateComponentData, navigateToStep, canNavigateToStep } =
        employeeTalkContext;

    const [activeStep, setActiveStep] = useState(currentStep || initialStep);

    const actualTemplate = template;
    if (!actualTemplate) {
        return <div>No template data available</div>;
    }

    const isOnboardingTemplate = isOnboarding ?? actualTemplate.templateCategory === ETemplateCategory.ONBOARDING;

    useEffect(() => {
        setActiveStep(currentStep || initialStep);
    }, [currentStep, initialStep]);

    const canProceedToNext = () => {
        return canNavigateToStep(activeStep + 1);
    };

    const handleNext = async () => {
        const nextStep = activeStep + 1;
        await navigateToStep(nextStep);
        setActiveStep(nextStep);
        if (onStepChange) {
            onStepChange(nextStep);
        }
    };

    const handleBack = async () => {
        const prevStep = activeStep - 1;
        await navigateToStep(prevStep);
        setActiveStep(prevStep);
        if (onStepChange) {
            onStepChange(prevStep);
        }
    };

    const handleStepClick = async (step: number) => {
        await navigateToStep(step);
        setActiveStep(step);
        if (onStepChange) {
            onStepChange(step);
        }
    };

    const handleSubmit = () => {
        if (onSubmit) {
            const allData: Record<string, unknown> = {};
            actualTemplate.pages.forEach((page) => {
                page.sections?.forEach((section) => {
                    section.columns?.forEach((column) => {
                        column.components?.forEach((component) => {
                            const data = getComponentData(component.id);
                            if (data !== undefined) {
                                allData[component.id] = data;
                            }
                        });
                    });
                });
            });
            onSubmit(allData);
        }
    };

    const renderComponent = (component: IEmployeeTalkTemplateComponent) => {
        const baseProps = {
            component,
            isEditing: false,
            onChange: (value: unknown) => {
                updateComponentData(component.id, value);
                if (component.type === 'cvUpload' && onCVUploadStateChange) {
                    const cvData = value as { files?: unknown[]; uploaded?: boolean } | undefined;
                    const isUploaded = cvData && ((cvData.files?.length ?? 0) > 0 || cvData.uploaded === true);
                    onCVUploadStateChange(activeStep, isUploaded || false);
                }
            },
            value: (() => {
                const componentData = getComponentData(component.id);
                if (componentData !== undefined) {
                    return componentData;
                }

                if (component.type === 'nameGender') {
                    const analysisDescription = talkState?.analysis?.description?.data;
                    return {
                        firstName: '',
                        lastName: '',
                        gender: EGender.OTHER,
                        description: analysisDescription || '',
                        phoneNumber: '',
                    };
                }

                return undefined;
            })(),
            employeeTalkId: employeeTalkId,
            pageIndex: activeStep,
            onNext: handleNext,
        };

        return getComponentByType(component.type, baseProps);
    };

    if (!actualTemplate.pages || actualTemplate.pages.length === 0) {
        return (
            <Box sx={{ p: 3, textAlign: 'center' }}>
                <Typography variant="h6" color="text.secondary">
                    Keine Seiten verfügbar
                </Typography>
            </Box>
        );
    }

    const currentPage = actualTemplate.pages[activeStep];
    if (!currentPage) {
        return (
            <Box sx={{ p: 3, textAlign: 'center' }}>
                <Typography variant="h6" color="text.secondary">
                    Seite nicht gefunden
                </Typography>
            </Box>
        );
    }

    return (
        <Box className={styles.templateEditor}>
            <Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
                    {currentPage.icon &&
                        (() => {
                            const IconComponent = Icons[currentPage.icon as keyof typeof Icons];
                            return IconComponent ? (
                                <Box
                                    sx={{
                                        width: 48,
                                        height: 48,
                                        borderRadius: '50%',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        bgcolor: 'primary.main',
                                        color: 'white',
                                    }}
                                >
                                    <IconComponent sx={{ fontSize: 24 }} />
                                </Box>
                            ) : null;
                        })()}
                    <Typography variant="h5" sx={{ fontWeight: 600 }}>
                        {currentPage.title}
                    </Typography>
                </Box>

                {currentPage.sections?.map((section: IEmployeeTalkTemplateSection) => (
                    <Box key={section.id} sx={{ mb: 4 }}>
                        {section.title && (
                            <Typography variant="h6" gutterBottom sx={{ fontWeight: 500, mb: 2 }}>
                                {section.title}
                            </Typography>
                        )}

                        <Box
                            sx={{
                                display: 'flex',
                                flexDirection: section.columns?.length > 1 ? 'row' : 'column',
                                gap: 3,
                            }}
                        >
                            {section.columns?.map((column: IEmployeeTalkTemplateColumn) => (
                                <Box
                                    key={column.id}
                                    sx={{
                                        flex: section.columns.length > 1 ? 1 : 'auto',
                                        display: 'flex',
                                        flexDirection: 'column',
                                        gap: 3,
                                    }}
                                >
                                    {column.components?.map((component: IEmployeeTalkTemplateComponent) => (
                                        <Box key={component.id}>{renderComponent(component)}</Box>
                                    ))}
                                </Box>
                            ))}
                        </Box>
                    </Box>
                ))}

                {actualTemplate.pages.length > 1 && (
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 4 }}>
                        <Button
                            text={oftenUsed.back}
                            onClick={handleBack}
                            disabled={activeStep === 0}
                            variant="outlined"
                        />
                        <Button
                            text={activeStep === actualTemplate.pages.length - 1 ? 'Abschließen' : oftenUsed.next}
                            onClick={activeStep === actualTemplate.pages.length - 1 ? handleSubmit : handleNext}
                            variant="contained"
                            disabled={!canProceedToNext()}
                        />
                    </Box>
                )}
            </Box>
        </Box>
    );
};

export default ETTTemplatePreview;
